import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Check, X, MessageSquare, Send, Edit, Rocket, AlertTriangle } from "lucide-react";

const STATUS_LABEL = {
  draft: "Draft",
  submitted: "Submitted",
  changes_requested: "Changes Requested",
  rejected: "Rejected",
  approved: "Approved",
  published: "Published",
  updated_after_publication: "Updated After Publication",
  archived: "Archived",
};

export default function ScheduleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [sched, setSched] = useState(null);
  const [error, setError] = useState(null);
  const [action, setAction] = useState(null); // {type, comments}
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api.get(`/schedules/${id}`)
      .then(({ data }) => setSched(data))
      .catch((e) => setError(e?.response?.status === 403
        ? "This schedule is not available to you. Employees can only view schedules once they are published."
        : formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="hos-card p-8 max-w-xl mx-auto text-center space-y-4" data-testid="schedule-forbidden">
        <div className="label-caps text-[color:var(--warning)]">Not available</div>
        <div>{error}</div>
        <button className="hos-btn-outline" onClick={() => nav("/schedules")}>Back to Schedules</button>
      </div>
    );
  }
  if (!sched) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const isGm = ["gm", "admin"].includes(user.role);
  const isManager = user.role === "manager";
  const isEmployee = user.role === "employee";
  const acknowledged = (sched.acknowledgments || []).some((a) => a.employee_id === user.id);

  const doAction = async () => {
    if (!action) return;
    if ((action === "reject" || action === "request_changes") && !comments.trim()) {
      toast.error("Comments required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/schedules/${id}/${action}`, { comments });
      toast.success("Done");
      setAction(null);
      setComments("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const acknowledge = async () => {
    try {
      await api.post(`/schedules/${id}/acknowledge`);
      toast.success("Thanks — schedule acknowledged");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const withdraw = async () => {
    if (!window.confirm("Withdraw submission and return to draft?")) return;
    try {
      await api.post(`/schedules/${id}/withdraw`);
      toast.success("Withdrawn");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shiftsByDay = (sched.shifts || []).reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const days = Object.keys(shiftsByDay).sort();

  return (
    <div className="space-y-6" data-testid="schedule-detail">
      <button onClick={() => nav(-1)} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back</button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-caps">Workforce</div>
          <h1 className="font-heading text-3xl sm:text-4xl">{sched.schedule_number}</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">
            Week of {sched.week_start} · {sched.department} · Created by {sched.created_by_name}
          </div>
        </div>
        <span className={`status-pill status-${sched.status === "published" ? "published" : sched.status === "approved" ? "open" : sched.status === "rejected" ? "error" : sched.status === "changes_requested" ? "progress" : "neutral"}`} data-testid="detail-status">
          {STATUS_LABEL[sched.status] || sched.status}
        </span>
      </div>

      {sched.changes_comments && (
        <div className="hos-card p-4 border-[color:var(--warning)]">
          <div className="label-caps text-[color:var(--warning)]">Changes Requested</div>
          <div className="mt-1 text-sm">{sched.changes_comments}</div>
        </div>
      )}
      {sched.rejection_reason && (
        <div className="hos-card p-4 border-[color:var(--error)]">
          <div className="label-caps text-[color:var(--error)]">Rejection reason</div>
          <div className="mt-1 text-sm">{sched.rejection_reason}</div>
        </div>
      )}
      {sched.approval_comments && sched.status === "approved" && (
        <div className="hos-card p-4 border-[color:var(--success)]">
          <div className="label-caps text-[color:var(--success)]">Approval comments</div>
          <div className="mt-1 text-sm">{sched.approval_comments}</div>
        </div>
      )}

      {/* Summary */}
      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">Summary</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Info k="Shifts" v={sched.summary?.shift_count || 0}/>
          <Info k="Employees" v={sched.summary?.employee_count || 0}/>
          <Info k="Total hours" v={(sched.summary?.total_hours || 0).toFixed(1)}/>
          <Info k="Warnings" v={(sched.warnings || []).length}/>
        </div>
        {(sched.warnings || []).length > 0 && (
          <div className="mt-4 p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-lg text-sm">
            <div className="flex items-center gap-2 text-[color:var(--warning)] font-semibold mb-2"><AlertTriangle size={16}/> Warnings</div>
            <ul className="list-disc pl-5">{sched.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}
      </div>

      {/* Shifts by day */}
      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">Shifts by Day</h2>
        {days.length === 0 && <div className="text-[color:var(--text-secondary)]">No shifts on this schedule.</div>}
        <div className="space-y-4">
          {days.map((d) => (
            <div key={d}>
              <div className="label-caps mb-2">{d}</div>
              <div className="grid gap-2">
                {shiftsByDay[d].map((s, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 p-3 border border-[color:var(--border)] rounded-lg">
                    <div className="min-w-[180px]">
                      <div className="text-[color:var(--text-primary)]">{s.employee_name || s.employee_id}</div>
                      <div className="text-xs text-[color:var(--text-secondary)]">{s.role} · {s.department}</div>
                    </div>
                    <div className="font-mono text-[color:var(--gold)]">{s.start_time} – {s.end_time}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">Break {s.break_minutes || 0}m · {s.shift_type}</div>
                    {s.notes && <div className="text-xs text-[color:var(--text-secondary)] italic">"{s.notes}"</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="hos-card p-6 flex flex-wrap gap-3 justify-end">
        {/* Manager actions */}
        {isManager && ["draft", "changes_requested"].includes(sched.status) && (
          <Link to={`/schedules/${sched.id}/edit`} className="hos-btn-outline flex items-center gap-2" data-testid="edit-schedule-btn"><Edit size={16}/> Edit</Link>
        )}
        {isManager && sched.status === "submitted" && (
          <button className="hos-btn-outline" onClick={withdraw} data-testid="withdraw-btn">Withdraw</button>
        )}

        {/* GM actions */}
        {isGm && sched.status === "submitted" && (
          <>
            <button className="hos-btn-outline" onClick={() => setAction("request_changes")} data-testid="request-changes-btn"><MessageSquare size={16} className="inline mr-1"/> Request Changes</button>
            <button className="hos-btn-outline" onClick={() => setAction("reject")} data-testid="reject-btn" style={{ borderColor: "var(--error)", color: "var(--error)" }}><X size={16} className="inline mr-1"/> Reject</button>
            <button className="hos-btn-primary" onClick={() => setAction("approve")} data-testid="approve-btn"><Check size={16} className="inline mr-1"/> Approve</button>
          </>
        )}
        {isGm && sched.status === "approved" && (
          <button className="hos-btn-primary" onClick={() => setAction("publish")} data-testid="publish-btn"><Rocket size={16} className="inline mr-1"/> Publish</button>
        )}

        {/* Employee ack */}
        {isEmployee && ["published", "updated_after_publication"].includes(sched.status) && (
          acknowledged
            ? <span className="status-pill status-open" data-testid="ack-status">Acknowledged</span>
            : <button className="hos-btn-primary" onClick={acknowledge} data-testid="ack-btn">I Have Seen This Schedule</button>
        )}
      </div>

      {/* Acknowledgments */}
      {["published", "updated_after_publication"].includes(sched.status) && (isGm || isManager) && (
        <div className="hos-card p-6">
          <h2 className="font-heading text-2xl mb-3">Acknowledgments</h2>
          <div className="text-sm text-[color:var(--text-secondary)] mb-2">{(sched.acknowledgments || []).length} of {sched.summary?.employee_count || 0} employees acknowledged</div>
          <ul className="text-sm space-y-1">
            {(sched.acknowledgments || []).map((a, i) => (
              <li key={i}>{a.employee_name} — {new Date(a.acknowledged_at).toLocaleString()}</li>
            ))}
          </ul>
        </div>
      )}

      {/* History */}
      <div className="hos-card p-6">
        <h2 className="font-heading text-2xl mb-3">History</h2>
        <ul className="space-y-2 text-sm">
          {(sched.history || []).map((h, i) => (
            <li key={i} className="border-l-2 border-[color:var(--gold)] pl-3">
              <div>{new Date(h.at).toLocaleString()} — {h.by_name} · <b>{h.action.replace(/_/g, " ")}</b></div>
              {h.comments && <div className="text-[color:var(--text-secondary)] italic">"{h.comments}"</div>}
            </li>
          ))}
        </ul>
      </div>

      {/* Action modal */}
      {action && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setAction(null)}>
          <div className="hos-card p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl mb-3">
              {action === "approve" && "Approve Schedule"}
              {action === "reject" && "Reject Schedule"}
              {action === "request_changes" && "Request Changes"}
              {action === "publish" && "Publish Schedule"}
            </h3>
            {action === "publish" && (
              <div className="text-sm text-[color:var(--text-secondary)] mb-3">
                {sched.summary?.employee_count || 0} employee(s) will be notified.
              </div>
            )}
            <textarea
              className="hos-input min-h-[100px]"
              placeholder={action === "reject" || action === "request_changes" ? "Reason (required)…" : "Optional comments…"}
              value={comments} onChange={(e) => setComments(e.target.value)}
              data-testid="action-comments"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button className="hos-btn-outline" onClick={() => setAction(null)} disabled={busy}>Cancel</button>
              <button className="hos-btn-primary" onClick={doAction} disabled={busy} data-testid="action-confirm-btn">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="label-caps">{k}</div>
      <div className="text-lg">{v}</div>
    </div>
  );
}
